import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BlueDotsBackground from '../../BlueDotsBackground';
import images from '../../content/dsaContent';

const automation = [
  {
    iconClass: 'fas fa-link',
    title: 'Integrated with Distributors',
    description:
      'Seamless integration with your key distribution partners ensures data is always connected and up to date.',
    image: images[0],
  },
  {
    iconClass: 'fas fa-chart-line',
    title: 'Real-Time Fee Calculation',
    description:
      'Calculate fees instantly using live data to minimize revenue leakage and overpayment risk.',
    image: images[1],
  },
  {
    iconClass: 'fas fa-money-bill-wave',
    title: 'Automated Payments',
    description:
      'Generate and send clean, compliant payments with audit-ready accuracy.',
    image: images[2],
  },
  {
    iconClass: 'fas fa-chart-bar',
    title: 'Scorecards & Reporting',
    description:
      'Access out-of-the-box scorecards and reports that keep your team aligned and your distributors informed.',
    image: images[3],
  },
];

const benefits = [
  {
    iconClass: 'fas fa-check-circle',
    title: 'No More Manual Errors',
    description:
      'Eliminate time-consuming, error-prone spreadsheets and reduce operational risk.',
  },
  {
    iconClass: 'fas fa-folder-open',
    title: 'One Central Hub',
    description:
      'All your DSA data in one place — searchable, centralized, and easy to manage.',
  },
];

const differentiators = [
  {
    iconClass: 'fas fa-exchange-alt',
    title: 'Easy Migration',
    description:
      'No matter where your data lives — spreadsheets, legacy systems, or third-party tools — OpenPharma makes the migration process easy, ensuring a smooth transition with minimal disruption.',
  },
  {
    iconClass: 'fas fa-lightbulb',
    title: 'Strategic Insights',
    description:
      'OpenPharma turns your distributor data into actionable insights with intuitive dashboards and reporting tools. Gain real-time visibility, improve understanding across teams, and accelerate decision-making.',
  },
];

export default function Dsa() {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedIndex((prev) => (prev + 1) % automation.length);
    }, 8000); // changes every 8 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <BlueDotsBackground />
      <section
        id="hero"
        className="relative text-center text-white overflow-hidden font-sans"
      >
        {/* //for screenshots - remove mx-auto on 88 and add text-left on 87 */}
        <div className="relative z-10 px-6 min-h-screen flex flex-col justify-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight leading-tight mb-8">
              Tired of managing distributor agreements in spreadsheets?
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 font-light mb-12">
              With over <strong>$15 billion</strong> paid annually in wholesaler
              fees, even small inefficiencies can lead to significant revenue
              leakage and compliance exposure.
            </p>
            <p className="text-lg md:text-xl text-gray-300 font-light mb-12">
              OpenPharma automates the entire DSA process, from pulling sales
              data to calculating fees, generating scorecards, and sending
              payments. The result is greater accuracy, stronger controls, and
              savings of up to 1% in total wholesaler spend.
            </p>
            <div className="max-w-xl w-full mx-auto mb-20">
              {/* Optional CTA button or form can go here */}
            </div>
          </div>
        </div>
      </section>

      <section id="automation" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Automated End-To-End Process
          </h2>
          <div className="h-[100px] flex items-center justify-center mb-10">
            <img
              src={automation[highlightedIndex].image}
              alt="Automation Step Visual"
              className="h-full object-contain mx-auto rounded-lg shadow-lg"
            />
          </div>

          {/* Dynamic description under image */}
          <div className="h-[60px] mb-10 flex items-center justify-center transition-all duration-300">
            <p className="text-gray-300 text-base md:text-lg max-w-3xl mx-auto">
              {automation[highlightedIndex].description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {automation.map((automation, idx) => (
              <div
                key={idx}
                className={`bg-gray-900 border rounded-xl p-6 shadow-md flex flex-col items-start gap-4 ${
                  highlightedIndex === idx ?
                    'border-blue-400 shadow-xl scale-105 transition-all'
                  : 'border-gray-700'
                }`}
              >
                <i
                  className={`${automation.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {automation.title}
                </h3>
                {/* <p className="text-gray-300 text-sm">{automation.description}</p> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="benefits" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i className={`${benefit.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="differentiators"
        className="py-16 px-6 text-white text-center"
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            What Sets Us Apart
          </h2>
          <div className="flex flex-wrap justify-center gap-6 text-left text-gray-800">
            {differentiators.map((item, idx) => (
              <div
                key={idx}
                className="relative bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                {/* Toggle Button */}
                <button
                  onClick={() =>
                    setExpandedIndex(expandedIndex === idx ? null : idx)
                  }
                  className="absolute top-4 right-4 text-blue-400 hover:text-blue-200 transition"
                  aria-label="Toggle Widget"
                >
                  <i
                    className={`fas ${expandedIndex === idx ? 'fa-minus' : 'fa-plus'} text-xl`}
                  />
                </button>

                {/* Icon and Text */}
                <i className={`${item.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {item.title}
                </h3>
                <p className="text-gray-300 text-sm">{item.description}</p>

                {/* Conditionally Rendered Dropdown Inside the Card */}
                {expandedIndex === idx && (
                  <div className="w-full mt-4 flex flex-col md:flex-row items-center justify-center gap-4">
                    {idx === 0 ?
                      <img
                        src={images[4]}
                        alt="customWidgetLeft"
                        className="w-full md:w-1/2 h-auto"
                      />
                    : <>
                        <img
                          src={images[5]}
                          alt="invWidget"
                          className="w-full md:w-1/2 h-[450px] border border-gray-700 rounded-lg shadow-lg"
                        />
                        <img
                          src={images[6]}
                          alt="pymtWidget"
                          className="w-full md:w-1/2 h-[450px] border border-gray-700 rounded-lg shadow-lg"
                        />
                      </>
                    }
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="cta" className="py-16 px-6 text-white text-center">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center gap-6">
          <Link to="/contact">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Contact Us
            </button>
          </Link>
          <Link to="/login">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Explore Our Platform
            </button>
          </Link>
        </div>
      </section>
    </>
  );
}
